import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight,
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Eye,
  ImageIcon,
  LoaderCircle,
  LockKeyhole,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CustomerFeedbackCard } from "@/components/customer-feedback-card";
import { JobCompanySelection } from "@/components/jobs/job-company-selection";
import {
  getManagedJob,
  manageManagedJob,
  saveManagedJobFeedback,
  type JobManagementAction,
  type ManagedJob,
} from "@/lib/job-management.functions";
import { useLanguage } from "@/lib/i18n";

export const Route = createFileRoute("/auftrag-verwalten")({
  staticData: { sitemap: false, toasts: true },
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token.slice(0, 256) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Auftrag sicher verwalten. Begner" },
      {
        name: "description",
        content: "Veröffentlichten Begner-Auftrag über den persönlichen Verwaltungslink verwalten.",
      },
      { name: "robots", content: "noindex, nofollow, noarchive" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
  component: ManageJobPage,
});

const TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,256}$/;

const managementCopy = {
  de: {
    locale: "de-CH",
    eyebrow: "Persönlicher Verwaltungsbereich",
    title: "Ihr Auftrag. Sicher in Ihrer Hand.",
    intro: "Hier sehen Sie den aktuellen Stand Ihres Inserats und entscheiden, wie es weitergeht.",
    security:
      "Dieser Bereich ist nur über Ihren persönlichen, zeitlich begrenzten Verwaltungslink erreichbar.",
    loading: "Auftrag wird sicher geladen…",
    invalidTitle: "Dieser Verwaltungslink ist nicht mehr gültig",
    invalidText:
      "Der Link ist möglicherweise unvollständig oder abgelaufen. Verwenden Sie den Link aus Ihrer Bestätigungs-E-Mail oder kontaktieren Sie uns.",
    invalidButton: "Zur Startseite",
    errorTitle: "Auftrag konnte nicht geladen werden",
    errorText: "Bitte prüfen Sie Ihre Verbindung und versuchen Sie es noch einmal.",
    retry: "Erneut versuchen",
    overview: "Übersicht",
    status: "Status",
    city: "Ausführungsort",
    published: "Veröffentlicht am",
    expires: "Online bis",
    remaining: (days: number) => (days === 1 ? "Noch 1 Tag online" : `Noch ${days} Tage online`),
    statusLabels: {
      published: "Online",
      hidden: "Nicht öffentlich",
      closed: "Als vergeben markiert",
      expired: "Abgelaufen",
      deleted: "Gelöscht",
    },
    viewListing: "Öffentliches Inserat ansehen",
    actionsTitle: "Auftrag verwalten",
    actionsText: "Jede Änderung wird erst nach Ihrer Bestätigung ausgeführt.",
    cancel: "Abbrechen",
    working: "Wird aktualisiert…",
    updateError: "Die Änderung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
    deletedTitle: "Auftrag gelöscht",
    deletedText:
      "Das Inserat ist nicht mehr öffentlich und die hinterlegten Kontaktdaten wurden entfernt.",
    actions: {
      extend: {
        title: "30 Tage verlängern",
        text: "Veröffentlichen Sie den Auftrag erneut beziehungsweise verlängern Sie die Laufzeit ab heute.",
        button: "Laufzeit verlängern",
        confirmTitle: "Auftrag um 30 Tage verlängern?",
        confirmText:
          "Der Auftrag wird ab heute für weitere 30 Tage öffentlich sichtbar. Die Interessenfrist und eine bestätigte Firmenauswahl bleiben bestehen. Kontaktdaten erhalten ausschliesslich die von Ihnen ausgewählten Firmen.",
        confirmButton: "Jetzt verlängern",
        success: "Der Auftrag wurde um 30 Tage verlängert.",
      },
      close: {
        title: "Als vergeben markieren",
        text: "Beenden Sie die Suche, sobald Sie den passenden Auftragnehmer gefunden haben.",
        button: "Auftrag als vergeben markieren",
        confirmTitle: "Auftrag wirklich als vergeben markieren?",
        confirmText:
          "Das Inserat wird geschlossen und nicht mehr öffentlich angezeigt. Sie können es später durch eine Verlängerung erneut veröffentlichen.",
        confirmButton: "Als vergeben markieren",
        success: "Der Auftrag wurde als vergeben markiert.",
      },
      delete: {
        title: "Auftrag löschen",
        text: "Entfernen Sie das Inserat und die zugehörigen Kontaktdaten dauerhaft aus dem Auftragsbereich.",
        button: "Auftrag dauerhaft löschen",
        confirmTitle: "Auftrag dauerhaft löschen?",
        confirmText:
          "Das Inserat wird entfernt und die hinterlegten Kontaktdaten werden gelöscht. Diese Aktion kann hier nicht rückgängig gemacht werden.",
        confirmButton: "Dauerhaft löschen",
        success: "Der Auftrag und die Kontaktdaten wurden gelöscht.",
      },
    },
  },
  fr: {
    locale: "fr-CH",
    eyebrow: "Espace de gestion personnel",
    title: "Votre mandat. En toute sécurité.",
    intro: "Consultez ici l’état actuel de votre annonce et décidez de la suite à lui donner.",
    security:
      "Cet espace est accessible uniquement via votre lien personnel de gestion à durée limitée.",
    loading: "Chargement sécurisé du mandat…",
    invalidTitle: "Ce lien de gestion n’est plus valable",
    invalidText:
      "Le lien est peut-être incomplet ou expiré. Utilisez le lien de votre e-mail de confirmation ou contactez-nous.",
    invalidButton: "Retour à l’accueil",
    errorTitle: "Impossible de charger le mandat",
    errorText: "Vérifiez votre connexion puis réessayez.",
    retry: "Réessayer",
    overview: "Aperçu",
    status: "Statut",
    city: "Lieu d’exécution",
    published: "Publié le",
    expires: "En ligne jusqu’au",
    remaining: (days: number) =>
      days === 1 ? "Encore 1 jour en ligne" : `Encore ${days} jours en ligne`,
    statusLabels: {
      published: "En ligne",
      hidden: "Non public",
      closed: "Marqué comme attribué",
      expired: "Expiré",
      deleted: "Supprimé",
    },
    viewListing: "Voir l’annonce publique",
    actionsTitle: "Gérer le mandat",
    actionsText: "Chaque modification n’est exécutée qu’après votre confirmation.",
    cancel: "Annuler",
    working: "Mise à jour…",
    updateError: "La modification n’a pas pu être enregistrée. Veuillez réessayer.",
    deletedTitle: "Mandat supprimé",
    deletedText:
      "L’annonce n’est plus publique et les coordonnées enregistrées ont été supprimées.",
    actions: {
      extend: {
        title: "Prolonger de 30 jours",
        text: "Republiez le mandat ou prolongez sa durée de 30 jours à compter d’aujourd’hui.",
        button: "Prolonger la durée",
        confirmTitle: "Prolonger le mandat de 30 jours?",
        confirmText:
          "Le mandat sera visible pendant 30 jours supplémentaires. Le délai d’intérêt et votre sélection confirmée restent inchangés. Seules les entreprises que vous avez choisies ont accès aux coordonnées.",
        confirmButton: "Prolonger maintenant",
        success: "Le mandat a été prolongé de 30 jours.",
      },
      close: {
        title: "Marquer comme attribué",
        text: "Mettez fin à la recherche dès que vous avez trouvé le prestataire qui vous convient.",
        button: "Marquer le mandat comme attribué",
        confirmTitle: "Marquer vraiment ce mandat comme attribué?",
        confirmText:
          "L’annonce sera fermée et ne sera plus visible publiquement. Une prolongation permettra de la republier ultérieurement.",
        confirmButton: "Marquer comme attribué",
        success: "Le mandat a été marqué comme attribué.",
      },
      delete: {
        title: "Supprimer le mandat",
        text: "Retirez définitivement l’annonce et les coordonnées associées de l’espace des mandats.",
        button: "Supprimer définitivement",
        confirmTitle: "Supprimer définitivement ce mandat?",
        confirmText:
          "L’annonce sera retirée et les coordonnées enregistrées seront supprimées. Cette action ne peut pas être annulée ici.",
        confirmButton: "Supprimer définitivement",
        success: "Le mandat et les coordonnées ont été supprimés.",
      },
    },
  },
  it: {
    locale: "it-CH",
    eyebrow: "Area di gestione personale",
    title: "Il suo incarico. In mani sicure.",
    intro: "Qui può consultare lo stato attuale dell’annuncio e decidere come procedere.",
    security:
      "Quest’area è accessibile solo tramite il suo link personale di gestione, valido per un periodo limitato.",
    loading: "Caricamento sicuro dell’incarico…",
    invalidTitle: "Questo link di gestione non è più valido",
    invalidText:
      "Il link potrebbe essere incompleto o scaduto. Utilizzi il link contenuto nell’e-mail di conferma oppure ci contatti.",
    invalidButton: "Alla pagina iniziale",
    errorTitle: "Impossibile caricare l’incarico",
    errorText: "Verifichi la connessione e riprovi.",
    retry: "Riprova",
    overview: "Panoramica",
    status: "Stato",
    city: "Luogo di esecuzione",
    published: "Pubblicato il",
    expires: "Online fino al",
    remaining: (days: number) =>
      days === 1 ? "Ancora 1 giorno online" : `Ancora ${days} giorni online`,
    statusLabels: {
      published: "Online",
      hidden: "Non pubblico",
      closed: "Contrassegnato come assegnato",
      expired: "Scaduto",
      deleted: "Eliminato",
    },
    viewListing: "Visualizza l’annuncio pubblico",
    actionsTitle: "Gestire l’incarico",
    actionsText: "Ogni modifica viene eseguita solo dopo la sua conferma.",
    cancel: "Annulla",
    working: "Aggiornamento…",
    updateError: "Non è stato possibile salvare la modifica. Riprovi.",
    deletedTitle: "Incarico eliminato",
    deletedText: "L’annuncio non è più pubblico e i dati di contatto salvati sono stati rimossi.",
    actions: {
      extend: {
        title: "Prolungare di 30 giorni",
        text: "Ripubblichi l’incarico o ne prolunghi la durata di 30 giorni a partire da oggi.",
        button: "Prolungare la durata",
        confirmTitle: "Prolungare l’incarico di 30 giorni?",
        confirmText:
          "L’incarico sarà visibile per altri 30 giorni. Il termine di interesse e la scelta confermata restano invariati. I contatti sono riservati alle imprese che hai selezionato.",
        confirmButton: "Prolunga ora",
        success: "L’incarico è stato prolungato di 30 giorni.",
      },
      close: {
        title: "Contrassegnare come assegnato",
        text: "Termini la ricerca non appena ha trovato l’impresa adatta.",
        button: "Contrassegna come assegnato",
        confirmTitle: "Contrassegnare davvero l’incarico come assegnato?",
        confirmText:
          "L’annuncio verrà chiuso e non sarà più visibile pubblicamente. Potrà ripubblicarlo in seguito prolungandone la durata.",
        confirmButton: "Contrassegna come assegnato",
        success: "L’incarico è stato contrassegnato come assegnato.",
      },
      delete: {
        title: "Eliminare l’incarico",
        text: "Rimuova definitivamente l’annuncio e i relativi dati di contatto dall’area incarichi.",
        button: "Elimina definitivamente",
        confirmTitle: "Eliminare definitivamente l’incarico?",
        confirmText:
          "L’annuncio verrà rimosso e i dati di contatto salvati saranno eliminati. Questa azione non può essere annullata qui.",
        confirmButton: "Elimina definitivamente",
        success: "L’incarico e i dati di contatto sono stati eliminati.",
      },
    },
  },
  en: {
    locale: "en-CH",
    eyebrow: "Personal management area",
    title: "Your job. Securely in your hands.",
    intro: "View the current status of your listing and decide what happens next.",
    security: "This area is accessible only through your personal, time-limited management link.",
    loading: "Securely loading job…",
    invalidTitle: "This management link is no longer valid",
    invalidText:
      "The link may be incomplete or expired. Use the link in your confirmation email or contact us.",
    invalidButton: "Back to home",
    errorTitle: "The job could not be loaded",
    errorText: "Please check your connection and try again.",
    retry: "Try again",
    overview: "Overview",
    status: "Status",
    city: "Work location",
    published: "Published on",
    expires: "Online until",
    remaining: (days: number) => (days === 1 ? "1 day remaining" : `${days} days remaining`),
    statusLabels: {
      published: "Online",
      hidden: "Not public",
      closed: "Marked as awarded",
      expired: "Expired",
      deleted: "Deleted",
    },
    viewListing: "View public listing",
    actionsTitle: "Manage job",
    actionsText: "Every change is made only after your confirmation.",
    cancel: "Cancel",
    working: "Updating…",
    updateError: "The change could not be saved. Please try again.",
    deletedTitle: "Job deleted",
    deletedText:
      "The listing is no longer public and the stored contact details have been removed.",
    actions: {
      extend: {
        title: "Extend by 30 days",
        text: "Republish the job or extend its duration by 30 days from today.",
        button: "Extend duration",
        confirmTitle: "Extend the job by 30 days?",
        confirmText:
          "The job will remain publicly visible for another 30 days. The interest deadline and any confirmed selection stay in place. Only the companies you selected can retrieve your contact details.",
        confirmButton: "Extend now",
        success: "The job has been extended by 30 days.",
      },
      close: {
        title: "Mark as awarded",
        text: "End your search once you have found the right contractor.",
        button: "Mark job as awarded",
        confirmTitle: "Really mark this job as awarded?",
        confirmText:
          "The listing will be closed and will no longer be public. You can republish it later by extending it.",
        confirmButton: "Mark as awarded",
        success: "The job has been marked as awarded.",
      },
      delete: {
        title: "Delete job",
        text: "Permanently remove the listing and its associated contact details from the jobs area.",
        button: "Delete permanently",
        confirmTitle: "Permanently delete this job?",
        confirmText:
          "The listing and stored contact details will be deleted. This action cannot be undone here.",
        confirmButton: "Delete permanently",
        success: "The job and contact details have been deleted.",
      },
    },
  },
} as const;

type ViewState = "loading" | "ready" | "invalid" | "error";
type DisplayStatus = ManagedJob["status"];

function ManageJobPage() {
  const { language } = useLanguage();
  const copy = managementCopy[language];
  const search = Route.useSearch();
  const token = search.token?.trim() ?? "";
  const loadManagedJob = useServerFn(getManagedJob);
  const updateManagedJob = useServerFn(manageManagedJob);
  const saveFeedback = useServerFn(saveManagedJobFeedback);
  const [viewState, setViewState] = useState<ViewState>("loading");
  const [job, setJob] = useState<ManagedJob | null>(null);
  const [working, setWorking] = useState<JobManagementAction | null>(null);

  const load = useCallback(async () => {
    if (!TOKEN_PATTERN.test(token)) {
      setJob(null);
      setViewState("invalid");
      return;
    }

    setViewState("loading");
    try {
      const result = await loadManagedJob({ data: { token } });
      setJob(result.job);
      setViewState(result.job ? "ready" : "invalid");
    } catch {
      setJob(null);
      setViewState("error");
    }
  }, [loadManagedJob, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const performAction = async (action: JobManagementAction) => {
    if (!job || working) return;
    setWorking(action);
    try {
      const result = await updateManagedJob({ data: { token, action } });
      if (!result.job) {
        setJob(null);
        setViewState("invalid");
        return;
      }
      setJob(result.job);
      toast.success(copy.actions[action].success);
    } catch {
      toast.error(copy.updateError);
    } finally {
      setWorking(null);
    }
  };

  if (viewState === "loading") return <LoadingState label={copy.loading} />;

  if (viewState === "invalid") {
    return (
      <MessageState
        icon={LockKeyhole}
        title={copy.invalidTitle}
        text={copy.invalidText}
        button={copy.invalidButton}
        href="/"
      />
    );
  }

  if (viewState === "error" || !job) {
    return (
      <MessageState
        icon={RefreshCw}
        title={copy.errorTitle}
        text={copy.errorText}
        button={copy.retry}
        onClick={() => void load()}
      />
    );
  }

  const status = effectiveStatus(job);
  const daysRemaining = remainingDays(job.expires_at);
  const image = job.images[0];
  const managementActions: Array<{
    action: JobManagementAction;
    icon: LucideIcon;
    tone: "brand" | "success" | "danger";
  }> = [
    { action: "extend", icon: CalendarPlus, tone: "brand" },
    { action: "close", icon: CheckCircle2, tone: "success" },
    { action: "delete", icon: Trash2, tone: "danger" },
  ];
  const actions = managementActions.filter(
    (item) =>
      (status !== "hidden" || item.action === "delete") &&
      !(status === "closed" && item.action === "close"),
  );

  return (
    <main className="relative isolate overflow-hidden bg-muted/25 py-10 sm:py-16 lg:py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-96 w-[75rem] -translate-x-1/2 rounded-full bg-brand-soft/80 blur-3xl" />
      <div className="container-page max-w-6xl">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-background/85 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-brand-deep shadow-sm backdrop-blur">
            <ShieldCheck className="size-4" />
            {copy.eyebrow}
          </span>
          <h1 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {copy.title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            {copy.intro}
          </p>
        </div>

        <Card className="mt-10 overflow-hidden border-border/70 bg-card/95 shadow-[0_28px_80px_-38px_hsl(var(--primary)/0.4)] backdrop-blur">
          <div className="grid lg:grid-cols-[minmax(300px,0.78fr)_minmax(0,1.22fr)]">
            <div className="relative min-h-64 overflow-hidden bg-gradient-hero lg:min-h-[390px]">
              {image ? (
                <img
                  src={image}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center text-white/70">
                  <ImageIcon className="size-16" strokeWidth={1.25} />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/20" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-8">
                <StatusBadge status={status} label={copy.statusLabels[status]} />
                <h2 className="mt-4 font-display text-2xl font-bold leading-tight sm:text-3xl">
                  {job.title}
                </h2>
                <p className="mt-3 flex items-center gap-2 text-sm text-white/85">
                  <MapPin className="size-4" />
                  {job.city}
                </p>
              </div>
            </div>

            <CardContent className="flex flex-col p-6 sm:p-8 lg:p-10">
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-deep">
                    {copy.overview}
                  </p>
                  <h2 className="mt-2 font-display text-2xl font-bold">{job.title}</h2>
                </div>
                {status === "published" && (
                  <Button asChild variant="outline" className="shrink-0 rounded-xl">
                    <a
                      href={`/auftraege/${encodeURIComponent(job.slug)}`}
                      referrerPolicy="no-referrer"
                    >
                      <Eye className="size-4" />
                      {copy.viewListing}
                    </a>
                  </Button>
                )}
              </div>

              <dl className="mt-8 grid gap-3 sm:grid-cols-2">
                <OverviewItem
                  icon={ClipboardCheck}
                  label={copy.status}
                  value={copy.statusLabels[status]}
                />
                <OverviewItem icon={MapPin} label={copy.city} value={job.city} />
                <OverviewItem
                  icon={Clock3}
                  label={copy.published}
                  value={formatDate(job.published_at, copy.locale)}
                />
                <OverviewItem
                  icon={CalendarClock}
                  label={copy.expires}
                  value={formatDate(job.expires_at, copy.locale)}
                />
              </dl>

              {status === "published" && daysRemaining > 0 && (
                <div className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-brand-soft px-3 py-1.5 text-xs font-bold text-brand-deep">
                  <Clock3 className="size-3.5" />
                  {copy.remaining(daysRemaining)}
                </div>
              )}

              <div className="mt-auto pt-8">
                <div className="flex items-start gap-3 rounded-2xl border border-primary/15 bg-brand-soft/55 p-4">
                  <LockKeyhole className="mt-0.5 size-5 shrink-0 text-primary" />
                  <p className="text-sm leading-relaxed text-foreground/80">{copy.security}</p>
                </div>
              </div>
            </CardContent>
          </div>
        </Card>

        {status === "deleted" ? (
          <Card className="mt-6 border-border/70 bg-card/95 shadow-sm">
            <CardContent className="flex flex-col items-center px-6 py-10 text-center sm:px-10">
              <span className="grid size-14 place-items-center rounded-2xl bg-muted text-muted-foreground">
                <Trash2 className="size-6" />
              </span>
              <h2 className="mt-5 font-display text-2xl font-bold">{copy.deletedTitle}</h2>
              <p className="mt-2 max-w-xl text-muted-foreground">{copy.deletedText}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mt-8">
              <JobCompanySelection
                key={`${job.id}-${job.status}-${job.expires_at}`}
                jobId={job.id}
                managementToken={token}
              />
            </div>
            {(status === "closed" || status === "expired") && (
              <div className="mt-8">
                <CustomerFeedbackCard
                  language={language}
                  initialFeedback={job.feedback}
                  onSave={async (rating, comment) => {
                    const result = await saveFeedback({
                      data: { jobId: job.id, token, rating, comment, locale: language },
                    });
                    setJob((current) =>
                      current ? { ...current, feedback: result.feedback } : current,
                    );
                    return result.feedback;
                  }}
                />
              </div>
            )}
            <section className="mt-10" aria-labelledby="management-actions-title">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                  <h2 id="management-actions-title" className="font-display text-2xl font-bold">
                    {copy.actionsTitle}
                  </h2>
                  <p className="mt-2 text-muted-foreground">{copy.actionsText}</p>
                </div>
                {working && (
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                    <LoaderCircle className="size-4 animate-spin" />
                    {copy.working}
                  </span>
                )}
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {actions.map(({ action, icon, tone }) => (
                  <ActionCard
                    key={action}
                    action={action}
                    icon={icon}
                    tone={tone}
                    copy={copy.actions[action]}
                    cancel={copy.cancel}
                    disabled={Boolean(working)}
                    onConfirm={performAction}
                  />
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <main className="relative grid min-h-[68vh] place-items-center overflow-hidden bg-muted/25 px-6 py-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-brand-soft/70 blur-3xl" />
      <div className="relative flex flex-col items-center text-center">
        <span className="grid size-16 place-items-center rounded-2xl border border-primary/15 bg-background text-primary shadow-card">
          <LoaderCircle className="size-7 animate-spin" />
        </span>
        <p className="mt-5 font-semibold text-muted-foreground">{label}</p>
      </div>
    </main>
  );
}

function MessageState({
  icon: Icon,
  title,
  text,
  button,
  href,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  text: string;
  button: string;
  href?: string;
  onClick?: () => void;
}) {
  return (
    <main className="relative grid min-h-[68vh] place-items-center overflow-hidden bg-muted/25 px-6 py-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-brand-soft/70 blur-3xl" />
      <Card className="relative w-full max-w-xl border-border/70 bg-card/95 shadow-card backdrop-blur">
        <CardContent className="flex flex-col items-center px-6 py-12 text-center sm:px-12">
          <span className="grid size-16 place-items-center rounded-2xl bg-brand-soft text-primary">
            <Icon className="size-7" />
          </span>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">{title}</h1>
          <p className="mt-3 max-w-md leading-relaxed text-muted-foreground">{text}</p>
          {href ? (
            <Button asChild size="lg" className="mt-7 rounded-xl shadow-brand">
              <a href={href} referrerPolicy="no-referrer">
                {button}
                <ArrowRight className="size-4" />
              </a>
            </Button>
          ) : (
            <Button size="lg" className="mt-7 rounded-xl shadow-brand" onClick={onClick}>
              <RefreshCw className="size-4" />
              {button}
            </Button>
          )}
        </CardContent>
      </Card>
    </main>
  );
}

function OverviewItem({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border/70 bg-muted/30 p-4">
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-background text-primary shadow-sm">
        <Icon className="size-4.5" />
      </span>
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 truncate text-sm font-bold text-foreground">{value}</dd>
      </div>
    </div>
  );
}

function StatusBadge({ status, label }: { status: DisplayStatus; label: string }) {
  const classes =
    status === "published"
      ? "bg-emerald-500/95 text-white hover:bg-emerald-500/95"
      : status === "closed"
        ? "bg-sky-500/95 text-white hover:bg-sky-500/95"
        : status === "deleted"
          ? "bg-slate-800/95 text-white hover:bg-slate-800/95"
          : status === "expired"
            ? "bg-amber-500/95 text-amber-950 hover:bg-amber-500/95"
            : "bg-background/95 text-foreground hover:bg-background/95";

  return <Badge className={`border-0 px-3 py-1 shadow-sm ${classes}`}>{label}</Badge>;
}

function ActionCard({
  action,
  icon: Icon,
  tone,
  copy,
  cancel,
  disabled,
  onConfirm,
}: {
  action: JobManagementAction;
  icon: LucideIcon;
  tone: "brand" | "success" | "danger";
  copy: {
    title: string;
    text: string;
    button: string;
    confirmTitle: string;
    confirmText: string;
    confirmButton: string;
  };
  cancel: string;
  disabled: boolean;
  onConfirm: (action: JobManagementAction) => Promise<void>;
}) {
  const iconClass =
    tone === "danger"
      ? "bg-destructive/10 text-destructive"
      : tone === "success"
        ? "bg-emerald-500/10 text-emerald-700"
        : "bg-brand-soft text-primary";

  return (
    <Card
      className={`flex h-full flex-col border-border/70 bg-card/95 shadow-sm transition hover:-translate-y-0.5 hover:shadow-card ${
        tone === "danger" ? "border-destructive/20" : ""
      }`}
    >
      <CardContent className="flex h-full flex-col p-5 sm:p-6">
        <span className={`grid size-11 place-items-center rounded-2xl ${iconClass}`}>
          <Icon className="size-5" />
        </span>
        <h3 className="mt-5 font-display text-lg font-bold">{copy.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{copy.text}</p>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant={tone === "danger" ? "outline" : "default"}
              className={`mt-6 w-full rounded-xl ${
                tone === "danger"
                  ? "border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
                  : ""
              }`}
              disabled={disabled}
            >
              <Icon className="size-4" />
              {copy.button}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-md rounded-2xl">
            <AlertDialogHeader>
              <div className={`mb-2 grid size-11 place-items-center rounded-2xl ${iconClass}`}>
                <Icon className="size-5" />
              </div>
              <AlertDialogTitle className="font-display text-xl">
                {copy.confirmTitle}
              </AlertDialogTitle>
              <AlertDialogDescription className="leading-relaxed">
                {copy.confirmText}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{cancel}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => void onConfirm(action)}
                className={
                  tone === "danger"
                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    : ""
                }
              >
                {copy.confirmButton}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

function effectiveStatus(job: ManagedJob): DisplayStatus {
  if (job.status !== "published") return job.status;
  const expiry = new Date(job.expires_at).getTime();
  return Number.isFinite(expiry) && expiry <= Date.now() ? "expired" : "published";
}

function remainingDays(value: string) {
  const expiry = new Date(value).getTime();
  if (!Number.isFinite(expiry)) return 0;
  return Math.max(0, Math.ceil((expiry - Date.now()) / (24 * 60 * 60 * 1000)));
}

function formatDate(value: string, locale: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "–";
  return new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(date);
}
